/**
 * 北海道の背景ツアー「北海道めぐり」コンテンツ。
 *
 * 福岡は「一日の時間帯」で景色を回したが、北海道は東西約500kmあり、
 * 一日で回る絵が成立しない。そこで軸を「季節」にして、
 * 春=朝 → 夏=昼 → 秋=夕 → 冬=夜 と並べることで、
 * 画面が徐々に暗くなる演出だけは福岡と同じまま保っている。
 *
 * 豆知識の本文と出典は claude/北海道データシート案.md に対応する。
 * 「書いてはいけないことリスト」に挙げた俗称・未確認の由来は入れていない。
 */

import type { StageTour } from '../types';

export const hokkaidoSceneryTour: StageTour = {
  scenes: [
    {
      id: 'goryokaku',
      name: '五稜郭の桜',
      area: '函館市・5月上旬の朝',
      image: '/assets/scenery/hokkaido/scenes/01_goryokaku.webp',
      hotspots: [
        {
          id: 'hakodate-sakura',
          x: 0.68,
          y: 0.22,
          title: '1,515本のソメイヨシノ',
          body: '五稜郭公園には1,515本のソメイヨシノが植えられています。始まりは大正3年（1914年）、函館毎日新聞社が発行1万号を記念して5千本の苗木を植えたことでした。毎年春になると、桜がいっせいに咲きます。',
          source: 'https://www.city.hakodate.hokkaido.jp/docs/2024032900136/',
          mapQuery: '五稜郭公園',
        },
        {
          id: 'hakodate-goryokaku',
          x: 0.36,
          y: 0.16,
          title: '星の形はヨーロッパ生まれ',
          body: '五稜郭は安政4年（1857年）に工事が始まり、元治元年（1864年）に完成しました。設計したのは蘭学者の武田斐三郎です。星の形は、箱館に来たフランス軍艦の軍人から西洋式の台場と土塁について教わったことをもとにしています。',
          source: 'https://www.city.hakodate.hokkaido.jp/docs/2014011700352/',
          mapQuery: '五稜郭公園',
        },
      ],
    },
    {
      id: 'biei',
      name: 'パッチワークの路',
      area: '美瑛町・6月の午前',
      image: '/assets/scenery/hokkaido/scenes/02_biei.webp',
      hotspots: [
        {
          id: 'biei-ken-merry',
          x: 0.62,
          y: 0.22,
          title: 'ケンとメリーの木',
          body: 'ケンとメリーの木は、昭和47年（1972年）に自動車のCMに登場した木です。名前はCMの登場人物にちなんでいます。となりの畑は私有地なので、立ち入らないようにしましょう。',
          source: 'https://www.biei-hokkaido.jp/ja/facility/ken-merry-tree',
          relatedItemId: 'imomochi',
          mapQuery: 'ケンとメリーの木',
        },
        {
          id: 'biei-rule',
          x: 0.28,
          y: 0.4,
          title: '畑は農家の仕事場',
          body: '美瑛町の丘に広がる畑や牧草地は、農家の私有地です。靴の裏についた菌や病害虫が農作物に病気を広げるため、美瑛町は畑や牧草地に立ち入らないよう定めています。',
          source: 'https://town.biei.hokkaido.jp/industry/kanko/rule.html',
          mapQuery: 'パッチワークの路',
        },
      ],
    },
    {
      id: 'furano',
      name: 'ファーム富田',
      area: '富良野市・7月の昼',
      image: '/assets/scenery/hokkaido/scenes/03_furano.webp',
      hotspots: [
        {
          id: 'furano-start',
          x: 0.3,
          y: 0.33,
          title: '10アールから始まった畑',
          body: 'ファーム富田のラベンダー栽培は、1958年に富田忠雄さんが10アールの畑をつくったことから本格的に始まりました。1970年には富良野地方全体で230ヘクタール以上、約250戸がラベンダーを育てるまでになりました。',
          source: 'https://www.farm-tomita.co.jp/history/',
          mapQuery: 'ファーム富田',
        },
        {
          id: 'furano-calendar',
          x: 0.78,
          y: 0.26,
          title: 'カレンダーが救った畑',
          body: '1972年、安い輸入香料が増えて、ラベンダーオイルの買い上げ価格が下がりました。翌年には香料会社が買い上げをやめてしまいます。ところが1976年、ラベンダー畑が国鉄のカレンダーで全国に紹介され、観光客が訪れるようになりました。',
          source: 'https://www.farm-tomita.co.jp/history/',
          mapQuery: 'ファーム富田',
        },
      ],
    },
    {
      id: 'shakotan',
      name: '神威岬',
      area: '積丹町・8月の昼過ぎ',
      image: '/assets/scenery/hokkaido/scenes/04_shakotan.webp',
      hotspots: [
        {
          id: 'shakotan-cape',
          x: 0.5,
          y: 0.3,
          title: '高さ80mの断崖の岬',
          body: '神威岬は、積丹半島から日本海に突き出した岬です。高さ80メートルの断崖絶壁に囲まれていて、先端までは約770メートルの遊歩道が続いています。先端までは歩いて約20分です。',
          source: 'https://www.visit-hokkaido.jp/spot/detail_10342.html',
          relatedItemId: 'uni-ikura-don',
          mapQuery: '神威岬',
        },
        {
          id: 'shakotan-charenka',
          x: 0.7,
          y: 0.13,
          title: '神威岩とチャレンカ',
          body: '岬の先にある神威岩には伝説が伝わっています。源義経を慕ったチャレンカが岬まで追いかけてきましたが、義経は海へ去ってしまい、チャレンカは海に身を投げて神威岩になったといわれます。以来この岬は女人禁制とされ、明治時代の初めまで続きました。',
          source: 'https://www.kanko-shakotan.jp/legend/',
          mapQuery: '神威岩',
        },
        {
          id: 'shakotan-gate',
          x: 0.28,
          y: 0.47,
          title: '風が強いと通れない道',
          body: '遊歩道には管理ゲートがあり、8月から10月は8時から17時30分まで開いています。風速15メートル以上の強風のときや大雨のときは、安全のためゲートが閉められます。冬のあいだはほとんど通れません。',
          source:
            'https://www.town.shakotan.lg.jp/contents/ddc41ae5350aa0780781c2fd36d25c563ed79e65.pdf',
          mapQuery: '神威岬',
        },
      ],
    },
    {
      id: 'kushiro',
      name: '釧路湿原',
      area: '釧路市・9月の午後',
      image: '/assets/scenery/hokkaido/scenes/05_kushiro.webp',
      hotspots: [
        {
          id: 'kushiro-largest',
          x: 0.6,
          y: 0.34,
          title: '日本一大きな湿原',
          body: '釧路湿原は日本でいちばん大きな湿原です。太平洋の海岸線から、いちばん奥では約40キロメートルも内陸に入りこんでいます。昭和62年（1987年）に国立公園に指定され、その面積は28,788ヘクタールです。',
          source: 'https://www.env.go.jp/park/kushiro/point/index.html',
          relatedItemId: 'zangi',
          mapQuery: '釧路湿原国立公園',
        },
        {
          id: 'kushiro-ramsar',
          x: 0.8,
          y: 0.2,
          title: '日本で最初のラムサール湿地',
          body: '釧路湿原は1980年に、日本で最初のラムサール条約の登録湿地になりました。登録されている面積は7,863ヘクタールです。日本で2番目に登録されたのは、1985年の伊豆沼・内沼でした。',
          source: 'https://www.env.go.jp/nature/ramsar/conv/RamsarSites_in_Japan.html',
          mapQuery: '釧路湿原国立公園',
        },
        {
          id: 'kushiro-tancho',
          x: 0.29,
          y: 0.41,
          title: 'タンチョウがもどってきた',
          body: 'タンチョウは国の特別天然記念物で、釧路湿原を代表する鳥です。乱獲や環境の変化で数が減り、一時は絶滅したと考えられたこともありましたが、その後の保護活動で数がもどってきました。いまは釧路湿原を中心とする北海道東部などで見ることができます。',
          source: 'https://www.env.go.jp/park/kushiro/point/index.html',
          mapQuery: '釧路湿原国立公園',
        },
      ],
    },
    {
      id: 'asahidake',
      name: '大雪山・旭岳',
      area: '東川町・9月下旬の夕方',
      image: '/assets/scenery/hokkaido/scenes/06_asahidake.webp',
      hotspots: [
        {
          id: 'asahidake-highest',
          x: 0.42,
          y: 0.14,
          title: '北海道でいちばん高い山',
          body: '旭岳は高さ2,291メートル。北海道でいちばん高い山で、いまも活動している火山です。まわりの大雪山国立公園は、陸の国立公園としては日本でいちばん大きな公園です。',
          source: 'https://www.mlit.go.jp/tagengo-db/R3-00250.html',
          mapQuery: '旭岳',
        },
        {
          id: 'asahidake-ropeway',
          x: 0.22,
          y: 0.33,
          title: 'ロープウェイで10分',
          body: '旭岳ロープウェイは、標高約1,100メートルの山麓駅から標高約1,600メートルの姿見駅まで、約10分でのぼります。ゴンドラは101人乗りの大型で、約500メートルの標高差を一気に上がります。',
          source: 'https://www.visit-hokkaido.jp/spot/detail_10355.html',
          mapQuery: '大雪山旭岳ロープウェイ',
        },
        {
          id: 'asahidake-koyo',
          x: 0.82,
          y: 0.34,
          title: '9月中旬に始まる秋',
          body: '大雪山の秋は、日本のどこよりも早い9月中旬に始まります。旭岳はいまも活動している火山で、山には火山の噴気孔があり、登山者に人気です。',
          source: 'https://www.mlit.go.jp/tagengo-db/R3-00250.html',
          mapQuery: '姿見の池',
        },
      ],
    },
    {
      id: 'otaru',
      name: '小樽運河',
      area: '小樽市・10月の日没後',
      image: '/assets/scenery/hokkaido/scenes/07_otaru.webp',
      hotspots: [
        {
          id: 'otaru-1923',
          x: 0.52,
          y: 0.4,
          title: '大正12年の運河',
          body: '小樽運河は1914年（大正3年）に埋め立て工事がはじまり、1923年（大正12年）に完成しました。海の沖を埋め立てて造ったので、ゆるやかに曲がっているのが特徴です。全長は1,140メートルあります。',
          source: 'https://www.city.otaru.lg.jp/docs/2023062000045/',
          mapQuery: '小樽運河',
        },
        {
          id: 'otaru-gaslight',
          x: 0.13,
          y: 0.2,
          title: 'ガス灯63基',
          body: '運河の散策路には、ガス灯が63基立っています。運河ぞいの石造りの倉庫は昔のままの姿で残り、今はレストランなどに使われています。夜はガス灯がともり、倉庫もライトアップされます。',
          source: 'https://www.city.otaru.lg.jp/docs/2020100900367/',
          mapQuery: '小樽運河',
        },
      ],
    },
    {
      id: 'hakodateyama',
      name: '函館山の夜景',
      area: '函館市・11月の夜',
      image: '/assets/scenery/hokkaido/scenes/08_hakodateyama.webp',
      hotspots: [
        {
          id: 'hakodateyama-334',
          x: 0.45,
          y: 0.29,
          title: '標高334メートル',
          body: '函館山の高さは334メートルです。牛が寝そべっているような形から「臥牛山」とも呼ばれます。',
          source: 'https://www.hakobura.jp/spots/494',
          mapQuery: '函館山展望台',
        },
        {
          id: 'hakodateyama-forbidden',
          x: 0.7,
          y: 0.25,
          title: '47年間、立入禁止だった山',
          body: '函館山には明治35年（1902年）に津軽海峡を守る要塞がつくられました。明治32年（1899年）からは、山に入ることも写真をとることもできませんでした。一般に開放されたのは、戦争が終わったあとの昭和21年（1946年）10月です。',
          source: 'https://www.hakobura.jp/spots/494',
          mapQuery: '函館山 御殿山第二砲台跡',
        },
      ],
    },
    {
      id: 'snowfes',
      name: 'さっぽろ雪まつり',
      area: '札幌市・2月の夜',
      image: '/assets/scenery/hokkaido/scenes/09_snowfes.webp',
      hotspots: [
        {
          id: 'snowfes-start',
          x: 0.26,
          y: 0.3,
          title: 'はじまりは雪像6つ',
          body: 'さっぽろ雪まつりは1950年にはじまりました。第1回は、市内の3つの高校と2つの中学校の生徒が大通公園に6つの雪像をつくったのがきっかけです。いちばん多かった年は2019年で、273万7千人が訪れました。',
          source: 'https://www.snowfes.com/about/history/',
          relatedItemId: 'sapporo-miso-ramen',
          mapQuery: '大通公園',
        },
        {
          id: 'snowfes-scale',
          x: 0.7,
          y: 0.23,
          title: '雪像210基・3つの会場',
          body: '会場は大通・すすきの・つどーむの3つです。2026年は全部で約210基の雪氷像がつくられました。いちばん多かったのは1992年の366基です。使う雪は10トントラックで約3,000台分になります。',
          source: 'https://www.snowfes.com/faq/',
          mapQuery: '大通公園',
        },
      ],
    },
    {
      id: 'ryuhyo',
      name: '網走の流氷',
      area: 'オホーツク・2月下旬の夜明け',
      image: '/assets/scenery/hokkaido/scenes/10_ryuhyo.webp',
      hotspots: [
        {
          id: 'ryuhyo-siberia',
          x: 0.2,
          y: 0.34,
          title: 'シベリアから来る氷',
          body: '流氷は12月のはじめに、シベリア大陸の沿岸で生まれます。そこから約1か月半かけて、北海道のオホーツク海沿岸の近くまでやってきます。',
          source: 'https://www.data.jma.go.jp/wakkanai/kishou/ryuhyou.html',
          relatedItemId: 'hamayude-kegani',
          mapQuery: 'オホーツク流氷館',
        },
        {
          id: 'ryuhyo-day',
          x: 0.65,
          y: 0.28,
          title: '流氷の目視観測が終わった',
          body: '稚内・網走・釧路の気象台は、「流氷初日」と「流氷接岸初日」を毎年、職員の目で観測してきました。この目視観測は令和7年度（2025年度）の冬のシーズンで終わり、気象衛星ひまわりなどの技術を使った情報に切り替わりました。',
          source: 'https://www.jma.go.jp/jma/press/2512/09e/20251209_seaice.html',
          mapQuery: 'オホーツク流氷館',
        },
        {
          id: 'ryuhyo-aurora',
          x: 0.38,
          y: 0.21,
          title: '砕氷船「おーろら」',
          body: '網走からは、流氷観光砕氷船「おーろら」が出ています。道の駅「流氷街道網走」から出港し、約60分かけて流氷の海をすすみます。運航は1月下旬から3月までです。',
          source: 'https://www.visit-hokkaido.jp/spot/detail_10045.html',
          mapQuery: '道の駅 流氷街道網走',
        },
      ],
    },
  ],
};
